import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Country } from './../../../../../account/entities/Country';
import { CustomsItemType } from './../../../../../send-receive/entities/CustomsItemType';
import { ErrorHelper } from './../../../../../shared/helpers/error-helper';
import { TranslationHelper } from './../../../../../shared/helpers/translation-helper';
import { HsCodeSuggestion, HsCodeSuggestionsResult } from './../../../../../send-receive/entities/HsCodeSuggestion';
import { HsCodeSuggestionsService } from './../../../../../send-receive/services/hs-code-suggestions.service';

@Component({
  selector: 'app-item-type',
  templateUrl: './item-type.component.html',
  styleUrls: ['./item-type.component.scss']
})
export class ItemTypeComponent implements OnInit {

  constructor(private formBuilder: FormBuilder, private errorHelper: ErrorHelper,
    private translate: TranslateService, private translateHelper: TranslationHelper,
    private hsCodeSuggestionsService: HsCodeSuggestionsService) { }

  @Input()
  countries: Array<Country>;

  @Input()
  index: number;
  
  @Input()  itemType!: CustomsItemType;
  @Output() itemTypeChange = new EventEmitter<CustomsItemType>();
  @Output() delete = new EventEmitter();

  public CheckValidity(){
    this.itemTypeForm.markAllAsTouched();
    return this.itemTypeForm.valid;
  }

  itemTypeForm: FormGroup;

  public suggestions: HsCodeSuggestion[] = [];
  public selectedSuggestion: HsCodeSuggestion;
  public suggestionLoading: boolean = false;
  public suggestionError: string;
  private suggestionRequestId: number = 0;

  public hasErrors(field) {
    var errors = this.itemTypeForm.get(field).errors;
    return !!errors;
  }

  public getError(field) {
    var errors = this.itemTypeForm.get(field).errors
    return this.errorHelper.getErrorMessage(errors);
  }

  ngOnInit(): void {
    this.itemTypeForm = this.formBuilder.group({
      description: [this.itemType.description, [Validators.required, Validators.maxLength(100)]],
      quantity: [this.itemType.quantity, [Validators.required, Validators.min(1), ErrorHelper.integerOnlyValidator]],
      weight: [this.itemType.netWeight, [Validators.required, ErrorHelper.numberOnlyValidator]],
      value: [this.itemType.value, [Validators.required, ErrorHelper.numberOnlyValidator]],
      country: [this.itemType.countryOfOrigin, []],
      tariffNo: [this.itemType.hsTariffNo, [ErrorHelper.nullableTariffValidator]]
    });

    this.translate.onLangChange.subscribe(x => {
      this.translateHelper.convertNumberField(this.itemTypeForm.get(`weight`));
      this.translateHelper.convertNumberField(this.itemTypeForm.get(`value`));
    });

    this.itemTypeForm.get('description').valueChanges.subscribe((newValue) => this.itemType.description = newValue);
    this.itemTypeForm.get('quantity').valueChanges.subscribe((newValue) => this.itemType.quantity = newValue);
    this.itemTypeForm.get('weight').valueChanges.subscribe((newValue) => 
    {
      this.translateHelper.formatNumberField(this.itemTypeForm.get('weight'));
      var weight = this.translateHelper.getNumber(newValue);
      this.itemType.netWeight = +(this.itemType.isKg ? weight : weight / 1000);
      
    });
    this.itemTypeForm.get('value').valueChanges.subscribe((newValue) => 
    {
      this.translateHelper.formatNumberField(this.itemTypeForm.get('value'));
      this.itemType.value = this.translateHelper.getNumber(newValue);
    });
    this.itemTypeForm.get('country').valueChanges.subscribe((newValue) => this.itemType.countryOfOrigin = newValue);
    this.itemTypeForm.get('tariffNo').valueChanges.subscribe((newValue) => this.itemType.hsTariffNo = newValue);
    this.itemTypeForm.get('description').valueChanges.subscribe(() => {
      this.suggestionError = undefined;
      if (this.selectedSuggestion) {
        this.selectedSuggestion = undefined;
        this.itemTypeForm.get('tariffNo').reset('', { emitEvent: false });
        this.itemType.hsTariffNo = undefined;
      }
    });
  }

  public requestSuggestions(): void {
    const descriptionControl = this.itemTypeForm.get('description');
    const description = (descriptionControl.value || '').trim();
    this.suggestions = [];
    this.suggestionError = undefined;

    if (!description) {
      descriptionControl.setErrors(Object.assign({}, descriptionControl.errors || {}, { required: true }));
      descriptionControl.markAsTouched();
      this.suggestionError = this.translate.instant('buy_postage.hs_code_suggestions.description_required');
      return;
    }

    descriptionControl.setErrors(null);
    const requestId = ++this.suggestionRequestId;
    this.suggestionLoading = true;
    this.hsCodeSuggestionsService.getSuggestions(description, this.getSuggestionLanguage()).subscribe(
      (result: HsCodeSuggestionsResult) => {
        if (requestId !== this.suggestionRequestId || description !== (descriptionControl.value || '').trim()) {
          return;
        }
        this.suggestionLoading = false;
        const insufficientMessage = this.getLocalizedValue(result && result.insufficientItemDescription);
        if (insufficientMessage) {
          this.suggestionError = insufficientMessage;
          return;
        }
        this.suggestions = (result && result.predictions || [])
          .slice()
          .sort((first, second) => second.confidence - first.confidence)
          .slice(0, 3);
      },
      (error: any) => {
        if (requestId !== this.suggestionRequestId) {
          return;
        }
        this.suggestionLoading = false;
        const insufficientMessage = this.getLocalizedValue(error && error.error && error.error.insufficientItemDescription);
        this.suggestionError = insufficientMessage || this.translate.instant('buy_postage.hs_code_suggestions.general_error');
      });
  }

  public selectSuggestion(suggestion: HsCodeSuggestion): void {
    this.selectedSuggestion = suggestion;
    this.itemType.hsTariffNo = suggestion.code;
    this.itemTypeForm.get('tariffNo').setValue(suggestion.code, { emitEvent: false });
    this.suggestionError = undefined;
  }

  public getSuggestionDescriptions(suggestion: HsCodeSuggestion): string[] {
    return suggestion && suggestion.descriptions ? Object.keys(suggestion.descriptions)
      .map(language => suggestion.descriptions[language]).filter(description => !!description) : [];
  }

  private getSuggestionLanguage(): string {
    return this.translate.currentLang === 'en' ? 'en' : 'fo';
  }

  private getLocalizedValue(values: { [language: string]: string }): string {
    if (!values) {
      return undefined;
    }
    return values[this.getSuggestionLanguage()] || values.en || values.fo || Object.keys(values)
      .map(language => values[language]).find(value => !!value);
  }
  
  public getCountryNameForLanguage(country: Country): string
  {
     switch (this.translate.currentLang) {
       case "en":
         return country.countryNameEN;
       case "fo":
         return country.countryNameFO;
     }
  }

  toggleKg(isKG: boolean){
    this.itemType.isKg = isKG;
    var weight = this.translateHelper.getNumber(this.itemTypeForm.get('weight').value);
    this.itemType.netWeight = +(this.itemType.isKg ? weight : weight / 1000);
  }

  public deleteType(){
    this.delete.emit();
  }
  

}
